import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, blueButtonClass, grayButtonClass, SmallTitleSpan, titleMRClass } from "src/components/common/common";
import { InputForm } from "src/components/common/form/input/InputForm";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useKSK1070_R01Store } from "./Store_KSK1070_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { KSK1070_R01LotDto, KSK1070_R01DetailDto } from "./Dto_KSK1070_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

const COLUMN_STATE_KEY = 'KSK1070_R01-lot-grid-column-state';

interface Props {
	lotGridRef: React.RefObject<AgGridReact<KSK1070_R01LotDto> | null>;
	defaultKSK1070_R01LotRow: KSK1070_R01LotDto;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const LotTable_KSK1070_R01 = (props: Props) => {

	const { lotGridRef, defaultKSK1070_R01LotRow, clickable, setClickable, handleInsertClick } = props;

	const {
		KSK1070_R01LotMap,
		setKSK1070_R01LotList,
		updateKSK1070_R01LotList,
		removeKSK1070_R01LotList,
		selectedKSK1070_R01LotList, addSelectedKSK1070_R01LotList, removeSelectedKSK1070_R01LotList,
		initSelectedKSK1070_R01LotList,
		deletedKSK1070_R01LotList, addDeletedKSK1070_R01LotList,
		originalKSK1070_R01LotList,
		selectedItemCdForLot,
		lotColumnState, setLotColumnState,
		lotScrollTop, setLotScrollTop,
		editKSK1070_R01,
		lotTotalQty,
		setLotTotalQty,
		applyLotTotalQty,
		purchaseLotNo, setPurchaseLotNo,
		selectedKSK1070_R01DetailList,
	} = useKSK1070_R01Store();

	const lotTotalQtyWrapperRef = useRef<HTMLDivElement>(null);
	const [lotQtyInputKey, setLotQtyInputKey] = useState(0);
	const isLotTotalQtyEditable = selectedKSK1070_R01DetailList.length > 0;
	const commitLotTotalQty = useCallback((qtyOverride?: number) => {
		const input = lotTotalQtyWrapperRef.current?.querySelector('input');
		const parsedQty = Number((input?.value ?? String(lotTotalQty)).replace(/,/g, '')) || 0;
		const qty = qtyOverride ?? parsedQty;
		if (applyLotTotalQty(qty)) {
			setLotQtyInputKey((key) => key + 1);
		}
	}, [lotTotalQty, applyLotTotalQty]);
	const { itemStatusList } = useCommonCodeStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const gridData = useMemo(
		() => KSK1070_R01LotMap.get(selectedItemCdForLot || '') || [],
		[KSK1070_R01LotMap, selectedItemCdForLot],
	);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef: lotGridRef,
		key: 'valueIndex',
		gridData,
		defaultGridData: defaultKSK1070_R01LotRow,
		selectedRow: selectedKSK1070_R01LotList,
		originalGridData: originalKSK1070_R01LotList,
		updateRow: (index: number, data: any) => updateKSK1070_R01LotList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		purchaseLotNo: 'Lot No',
		lotNo: 'LOT No',
		lotQty: '수량',
		lotInputOption: 'LOT입력옵션',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['purchaseLotNo'],
		insertedDisabledCells: ['lotNo'],
		commonDisabledCells: ['lotNo'],
		
		pointerCells: ['lotQty', 'lotInputOption'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<KSK1070_R01LotDto>({
            idKey: 'valueIndex',
            selectedList: selectedKSK1070_R01LotList,
            deletedList: deletedKSK1070_R01LotList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: {
						...baseRules,
						// 'disabled-cell, pointer-cursor' 규칙 재정의 (비활성화 처리)
						// [예외 처리] lotInputOption이 'Y'이고 컬럼이 'lotNo'라면 -> 비활성화 해제(false)
						// 그 외에는 기본 규칙 실행
						'disabled-cell': (params: any) => {
							if (params.data?.lotInputOption === 'Y' && (params.colDef.field === 'lotNo' || params.colDef.colId === 'lotNo')) { return false; }							
							return baseRules['disabled-cell'](params);
						},
						'pointer-cursor': (params: any) => {
							if (params.data?.lotInputOption === 'Y' && (params.colDef.field === 'lotNo' || params.colDef.colId === 'lotNo')) { return true; }
							return baseRules['pointer-cursor'](params);
						}
					}
				};
    }, [
		
        selectedKSK1070_R01LotList, deletedKSK1070_R01LotList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	const commonInputColumns = ['lotNo', 'lotQty', 'lotInputOption'];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['lotNo', 'lotQty', 'lotInputOption'];

	const baseColumnDefs: ColDef<KSK1070_R01LotDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: KSK1070_R01DetailDto) => renderIndexCell(
				params, 
				(innerParams: KSK1070_R01LotDto) => getRowStyleFlags(
					innerParams,
					{
						deletedList: deletedKSK1070_R01LotList,
						selectedList: selectedKSK1070_R01LotList,
						dataList: KSK1070_R01LotMap.get(selectedItemCdForLot || '') || []
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
					} else { 
						handleInsertClick(e, rowIndex);
					}
				},
				(rowData) => RemoveRowHandler( 
					lotGridRef,
					gridData, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removeKSK1070_R01LotList, 
					(valueIndex) => addDeletedKSK1070_R01LotList([valueIndex.toString()])
				),
			)
		),
		{
			headerName: 'Lot No', colId: 'purchaseLotNo', field: 'purchaseLotNo', width: 130,
			editable: false,
		},
		{
			headerName: '입고량', colId: 'lotQty', field: 'lotQty', width: 85,
			editable: (params: any) => params.data.inserted === true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,

		},
		// {
		// 	headerName: '직접입력', colId: 'lotInputOption', field: 'lotInputOption', width: 75,
		// 	...getCommonCodeColumnOptions(yesNoList, (params: any) => params.data.inserted === true, 'string'),
		// },
        {
            headerName: 'LOT No', colId: 'lotNo', field: 'lotNo', width: 170,
			editable: (params: any) => params.data.lotInputOption === 'Y',
		},
		// {
		// 	headerName: '출력 여부', colId: 'printed', field: 'printed', width: 50,
		// 	editable: false,
		// 	cellRenderer: (params: any) => params.value ? 'V' : '',
		// 	cellStyle: (params: any) => ({
		// 		textAlign: 'center',
		// 		fontWeight: params.value ? 'bold' : 'normal',
		// 		color: params.value ? '#10b981' : '#6b7280'
		// 	}),
		// },
		// ...IndexParameterCell(),
	], [
		deletedKSK1070_R01LotList, gridData, selectedItemCdForLot, selectedKSK1070_R01LotList, handleInsertClick, createCellClassRules,
	]);

	
const columnDefs: ColDef<KSK1070_R01LotDto>[] = useMemo(() => {
		if (lotColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<KSK1070_R01LotDto>({
			baseColumnDefs,
			columnState: lotColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, lotColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: gridData,
		originalDataList: originalKSK1070_R01LotList,
		updateRow: updateKSK1070_R01LotList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: gridData,
		idKey: 'valueIndex',
		selectedList: selectedKSK1070_R01LotList,
		deletedList: deletedKSK1070_R01LotList,
		addSelected: (ids) => {
			// console.log(' LOT 선택 추가:', ids);
			addSelectedKSK1070_R01LotList(ids);
		},
		removeSelected: (ids) => {
			// console.log(' LOT 선택 해제:', ids);
			removeSelectedKSK1070_R01LotList(ids);
		},
		clickable, setClickable
	});


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: gridData as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<KSK1070_R01LotDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(gridData, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK1070_R01LotDto>[] = useMemo(() => [
	], [editKSK1070_R01?.warehouseCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(gridData, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<KSK1070_R01LotDto>) => {
		postSortHandler(params, gridData, originalKSK1070_R01LotList, setKSK1070_R01LotList);
	}, [gridData, originalKSK1070_R01LotList, setKSK1070_R01LotList]);

	const pinnedBottomRowData = useMemo(() => {
		return [{
			lotNo: gridData.length.toLocaleString() + "건",
			lotQty: gridData.reduce((sum, item) => sum + (deletedKSK1070_R01LotList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0),
		}];	  
	}, [gridData, deletedKSK1070_R01LotList,]);

	// 선택된 LOT 행 스타일 (출력 대기 표시)
	const getRowStyle = useCallback((params: any) => {
		if (!params.data || params.node.rowPinned) return undefined;
		
		const valueIndex = String(params.data.valueIndex);
		const isSelectedForPrint = selectedKSK1070_R01LotList.includes(valueIndex);
		const isPrinted = params.data.printed === true;
		
		// console.log(' getRowStyle 실행:', { valueIndex, isSelectedForPrint, isPrinted });
		
		// if (isSelectedForPrint && !isPrinted) {
		// 	// 출력 대기 상태 (선택됨 + 미출력)
		// 	return { 
		// 		backgroundColor: '#e3f2fd !important',  // 연한 파란색 (우선순위 높임)
		// 		borderLeft: '3px solid #2196f3'
		// 	};
		// }
		
		return undefined;
	}, [selectedKSK1070_R01LotList]);

	return (
		<div className="flex flex-1 gap-[4px] h-full min-h-full">
			<GridContextWrapper<KSK1070_R01LotDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={lotGridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setKSK1070_R01LotList}
				initSelected={initSelectedKSK1070_R01LotList}
				defaultRowData={defaultKSK1070_R01LotRow}
				baseColumnDefs={columnDefs}
				setColumnState={setLotColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<div className="w-full h-[32px] shrink-0 flex items-center gap-[8px]">
					<div className="h-[32px] shrink-0 [&>span]:!w-auto [&>span]:!h-[32px] [&>span]:!min-h-[32px] [&>span]:!max-h-[32px] [&>span]:!float-none [&>span]:!py-0 [&>span]:box-border">
						<SmallTitleSpan>입고 Lot 입력</SmallTitleSpan>
					</div>
					<InputForm
						title="구매Lot"
						value={purchaseLotNo}
						editable={true}
						name="purchaseLotNo"
						onChange={(e) => setPurchaseLotNo(e.target.value)}
						wrapperOption={{
							wrapperStyle: "w-[200px] min-w-[200px] mb-0 items-center",
							titleStyle: "w-[80px] min-w-[80px] text-left",
							inputStyle: "w-[110px] min-w-[110px]",
						}}
					/>
					<div ref={lotTotalQtyWrapperRef} className="flex items-center gap-[4px] h-[32px]">
						<InputForm
							key={lotQtyInputKey}
							title="주문잔량"
							value={lotTotalQty}
							editable={isLotTotalQtyEditable}
							onChange={(e) => {
								const qty = Number(e.target.value.replace(/,/g, '')) || 0;
								setLotTotalQty(qty);
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault();
									e.stopPropagation();
									const qty = Number(String((e.target as HTMLInputElement).value ?? '').replace(/,/g, '')) || 0;
									commitLotTotalQty(qty);
								}
							}}
							wrapperOption={{
								wrapperStyle: "w-[170px] min-w-[170px] mb-0 items-center",
								titleStyle: "w-[90px] min-w-[90px] text-left",
								inputStyle: "w-[70px] min-w-[70px]",
							}}
						/>
						<button
							type="button"
							onClick={() => commitLotTotalQty()}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault();
								}
							}}
							disabled={!isLotTotalQtyEditable}
							className={`h-[32px] shrink-0 rounded-[4px] px-[10px] text-[12px] font-semibold leading-none text-white ${
								isLotTotalQtyEditable ? blueButtonClass : grayButtonClass
							}`}
						>
							적용
						</button>
					</div>
				</div>
				<Grid_Excel<KSK1070_R01LotDto>
					ref={lotGridRef}
					rowData={paddedGridData}	getRowId={getRowId}
					columnDefs={columnDefs}		
					
					defaultColDef={cellCssDefaultColDef}
					rowSelection="multiple"

					rowClassRules={createRowClassRules<KSK1070_R01LotDto>({
						idKey: 'valueIndex',
						selectedList: selectedKSK1070_R01LotList,
						deletedList: deletedKSK1070_R01LotList
					})}
					getRowStyle={getRowStyle}

					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					

					onPostSortRows={handlePostSort}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setLotColumnState}
				/>
			</GridContextWrapper>
		</div>
	)
}	
